// src/components/SettingsView.tsx
import React, { useState } from 'react';
import { 
    Briefcase, 
    User, 
    Trash2, 
    CreditCard, 
    ClipboardCopy, 
    ArrowLeft, 
    Settings, 
    Database,
    CheckSquare,
    Upload,
    Image as ImageIcon,
    Loader2,
    CalendarCheck // Nieuw icoon voor reserveringen
} from 'lucide-react';

interface SettingsViewProps {
    user: any;
    userProfile: any;
    stringers: any[];
    jobs: any[];
    customLinkId: string;
    
    draftCompanyName: string;
    setDraftCompanyName: (val: string) => void;
    saveCompanyName: () => void;
    
    newStringerName: string;
    setNewStringerName: (val: string) => void;
    addStringer: (e: React.FormEvent) => void;
    deleteStringer: (id: string) => void;
    
    draftLinkId: string;
    setDraftLinkId: (val: string) => void;
    saveCustomLinkId: () => void;
    
    // AANGEPAST: Mollie gesplitst in Live en Test voor de webshop-reserveringen
    mollieLiveKey: string;
    setMollieLiveKey: (val: string) => void;
    mollieTestKey: string;
    setMollieTestKey: (val: string) => void;
    saveMollieKeys: () => void;
    
    setSelectedStringerPortal: (name: string) => void;
    setActiveTab: (tab: string) => void;
    setImportText: (val: string) => void;
    setShowJobImportModal: (val: boolean) => void;
    
    // Formulier instellingen
    formSettings: any;
    updateFormSettings: (settings: any) => void;

    // Logo functionaliteit
    companyLogo?: string;
    isUploadingLogo?: boolean;
    onLogoUpload?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

// AANGEPAST: 'testrackets' toegevoegd aan de categorieën
type SettingCategory = 'company' | 'stringers' | 'mollie' | 'requests' | 'data' | 'testrackets' | null;

export default function SettingsView({
    user, userProfile, stringers, jobs, customLinkId,
    draftCompanyName, setDraftCompanyName, saveCompanyName,
    newStringerName, setNewStringerName, addStringer, deleteStringer,
    draftLinkId, setDraftLinkId, saveCustomLinkId,
    mollieLiveKey, setMollieLiveKey, mollieTestKey, setMollieTestKey, saveMollieKeys,
    setSelectedStringerPortal, setActiveTab,
    setImportText, setShowJobImportModal,
    formSettings, updateFormSettings,
    companyLogo, isUploadingLogo, onLogoUpload
}: SettingsViewProps) {
    
    const [activeSetting, setActiveSetting] = useState<SettingCategory>(null);

    const getShareLink = () => {
        let path = typeof window !== 'undefined' ? window.location.pathname : '';
        if (path.endsWith('/')) path = path.slice(0, -1);
        return `${window.location.origin}${path}?page=aanmelden&id=${customLinkId || user?.uid}`;
    };
    const shareLink = getShareLink();

// NIEUW: De unieke link voor het reserveren van testrackets
    const getReservationLink = () => {
        let path = typeof window !== 'undefined' ? window.location.pathname : '';
        if (path.endsWith('/')) path = path.slice(0, -1);
        return `${window.location.origin}${path}?page=reserveer&id=${customLinkId || user?.uid}`;
    };
    const reservationLink = getReservationLink();

    const FormToggle = ({ label, desc, checked, onChange }: { label: string, desc: string, checked: boolean, onChange: (val: boolean) => void }) => (
        <div 
            className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-700 cursor-pointer hover:border-indigo-200 dark:hover:border-indigo-800 transition"
            onClick={(e) => {
                e.preventDefault();
                onChange(!checked);
            }}
        >
            <div className="pr-4 pointer-events-none">
                <p className="font-bold text-slate-800 dark:text-white text-sm">{label}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{desc}</p>
            </div>
            <div 
                className={`shrink-0 w-12 h-6 rounded-full transition-colors relative pointer-events-none ${checked ? 'bg-indigo-600' : 'bg-slate-300 dark:bg-slate-600'}`}
            >
                <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-300 shadow-sm ${checked ? 'translate-x-6' : 'translate-x-0'}`} />
            </div>
        </div>
    );

    const renderDetailView = () => {
        if (!activeSetting) return null;

        let title = '';
        let icon = null;
        let content = null;

        switch (activeSetting) {
            case 'company':
                title = 'Bedrijfsinformatie';
                icon = <Briefcase className="w-6 h-6 text-indigo-500" />;
                content = (
                    <div className="space-y-6">
                        <p className="text-sm text-slate-500 dark:text-slate-400">Deze naam en het logo worden gebruikt als afzender in betaalverzoeken, het menu en op het aanmeldformulier voor klanten.</p>
                        
                        <div>
                            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Naam Organisatie / Bespanner</label>
                            <input 
                                type="text" 
                                placeholder="Jouw bedrijfsnaam" 
                                className="w-full p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
                                value={draftCompanyName}
                                onChange={(e) => setDraftCompanyName(e.target.value)}
                            />
                        </div>

                        <div className="pt-4 border-t border-slate-100 dark:border-slate-700">
                            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-3">Bedrijfslogo</label>
                            <div className="flex items-center gap-4">
                                <div className="w-20 h-20 rounded-xl bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 flex items-center justify-center overflow-hidden shrink-0">
                                    {companyLogo ? (
                                        <img src={companyLogo} alt="Bedrijfslogo" className="w-full h-full object-contain p-1" />
                                    ) : (
                                        <ImageIcon className="w-8 h-8 text-slate-400" />
                                    )}
                                </div>
                                <div className="flex-1">
                                    <input 
                                        type="file" 
                                        accept="image/png, image/jpeg, image/jpg" 
                                        id="logo-upload"
                                        className="hidden"
                                        onChange={onLogoUpload}
                                        disabled={isUploadingLogo}
                                    />
                                    <label 
                                        htmlFor="logo-upload"
                                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition ${isUploadingLogo ? 'bg-slate-200 text-slate-500 cursor-not-allowed dark:bg-slate-800' : 'cursor-pointer bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50'}`}
                                    >
                                        {isUploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                        {isUploadingLogo ? 'Bezig met uploaden...' : 'Kies een afbeelding'}
                                    </label>
                                    <p className="text-xs text-slate-500 mt-2">Aanbevolen: Vierkante PNG of JPG met transparante achtergrond (max 2MB).</p>
                                </div>
                            </div>
                        </div>

                        <button onClick={saveCompanyName} className="w-full bg-indigo-600 hover:bg-indigo-700 transition text-white py-3 rounded-xl font-bold mt-2">Informatie Opslaan</button>
                    </div>
                );
                break;

            case 'stringers':
                title = 'Bespanners Profielen';
                icon = <User className="w-6 h-6 text-emerald-500" />;
                content = (
                    <div>
                        <form onSubmit={addStringer} className="flex gap-2 mb-6">
                            <input 
                                type="text" 
                                placeholder="Naam bespanner..." 
                                className="flex-1 p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
                                value={newStringerName}
                                onChange={(e) => setNewStringerName(e.target.value)}
                            />
                            <button className="bg-indigo-600 hover:bg-indigo-700 transition text-white px-6 rounded-xl font-bold shadow-sm">Toevoegen</button>
                        </form>
                        <ul className="space-y-3">
                            {stringers.map(s => {
                                const stringerJobs = jobs.filter(j => j.stringer === s.name);
                                const todo = stringerJobs.filter(j => j.status !== 'Klaar').length;
                                const done = stringerJobs.filter(j => j.status === 'Klaar').length;
                                
                                return (
                                <li 
                                    key={s.id} 
                                    onClick={() => {
                                        setSelectedStringerPortal(s.name);
                                        setActiveTab('stringer-portal');
                                    }}
                                    className="flex justify-between items-center p-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-700/50 hover:border-indigo-300 dark:hover:border-indigo-700 cursor-pointer transition-all group"
                                >
                                    <div>
                                        <span className="font-bold text-slate-800 dark:text-white block">{s.name}</span>
                                        <span className="text-xs text-slate-500 flex gap-2 mt-1">
                                            <span className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2 py-0.5 rounded-md">{done} gedaan</span>
                                            <span className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 px-2 py-0.5 rounded-md">{todo} te doen</span>
                                        </span>
                                    </div>
                                    <button onClick={(e) => { e.stopPropagation(); deleteStringer(s.id); }} className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg transition" title="Verwijder bespanner">
                                        <Trash2 className="w-5 h-5" />
                                    </button>
                                </li>
                                );
                            })}
                            {stringers.length === 0 && <p className="text-slate-400 text-sm text-center py-8 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-dashed border-slate-200 dark:border-slate-700">Nog geen bespanners toegevoegd.</p>}
                        </ul>
                    </div>
                );
                break;

            case 'mollie':
                title = 'Mollie API (Betaallinks & Borg)';
                icon = <CreditCard className="w-6 h-6 text-blue-500" />;
                content = (
                    <div className="space-y-6">
                        <p className="text-sm text-slate-500 dark:text-slate-400">Vul hier je Mollie API sleutels in om automatisch betaalverzoeken te kunnen sturen én borg voor testrackets te ontvangen.</p>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Mollie Live API Key</label>
                                <input 
                                    type="password" 
                                    placeholder="live_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" 
                                    className="w-full p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
                                    value={mollieLiveKey}
                                    onChange={(e) => setMollieLiveKey(e.target.value)}
                                />
                                <p className="text-[10px] text-slate-400 mt-1">Wordt gebruikt voor daadwerkelijke betalingen.</p>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Mollie Test API Key</label>
                                <input 
                                    type="password" 
                                    placeholder="test_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" 
                                    className="w-full p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
                                    value={mollieTestKey}
                                    onChange={(e) => setMollieTestKey(e.target.value)}
                                />
                                <p className="text-[10px] text-slate-400 mt-1">Optioneel: om de testracket-reserveringen eerst zelf veilig te testen.</p>
                            </div>
                        </div>

                        <button onClick={saveMollieKeys} className="w-full bg-indigo-600 hover:bg-indigo-700 transition text-white py-3 rounded-xl font-bold">API Sleutels Opslaan</button>
                    </div>
                );
                break;

            case 'testrackets':
                title = 'Testrackets & Reserveringen';
                icon = <CalendarCheck className="w-6 h-6 text-purple-500" />;
                content = (
                    <div className="space-y-6">
                        <p className="text-sm text-slate-500 dark:text-slate-400">Deel deze link met je klanten (bijv. via je website of Instagram) zodat ze online een testracket kunnen reserveren en direct de borg (€25) kunnen betalen.</p>
                        
                        <div>
                            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Jouw Publieke Reserveringslink</label>
                            <div className="flex gap-2">
                                <input 
                                    type="text" 
                                    readOnly 
                                    value={reservationLink} 
                                    className="flex-1 p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 text-sm focus:outline-none"
                                />
                                <button 
                                    onClick={() => {
                                        const textArea = document.createElement("textarea");
                                        textArea.value = reservationLink;
                                        document.body.appendChild(textArea);
                                        textArea.select();
                                        try {
                                            document.execCommand('copy');
                                            alert("Reserveringslink is gekopieerd!");
                                        } catch (err) {
                                            console.error('Kopiëren mislukt', err);
                                        }
                                        document.body.removeChild(textArea);
                                    }} 
                                    className="px-5 py-3 bg-purple-100 hover:bg-purple-200 dark:bg-purple-900/30 dark:hover:bg-purple-900/50 text-purple-700 dark:text-purple-400 rounded-xl font-bold transition flex items-center justify-center"
                                >
                                    <ClipboardCopy className="w-5 h-5 mr-2" />
                                    Kopieer
                                </button>
                            </div>
                            <p className="text-[10px] text-slate-400 mt-2">Zorg dat je je Mollie API keys hebt ingevuld (onder Mollie API) voordat je deze link deelt, anders werkt de reservering niet!</p>
                        </div>
                    </div>
                );
                break;

            case 'requests':
                title = 'Aanvragen Accepteren';
                icon = <CheckSquare className="w-6 h-6 text-pink-500" />;
                content = (
                    <div className="space-y-8">
                        <div>
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-3">Formulier Velden</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Kies welke vragen klanten te zien krijgen als ze via jouw link een aanvraag doen. Wijzigingen worden direct opgeslagen.</p>
                            
                            <div className="space-y-3">
                                <FormToggle 
                                    label="Vraag om E-mailadres" 
                                    desc="Vraag het e-mailadres zodat je klanten later statusupdates kunt mailen."
                                    checked={!!formSettings?.askEmail}
                                    onChange={(val) => updateFormSettings({ ...(formSettings || {}), askEmail: val })}
                                />
                                <FormToggle 
                                    label="Vraag om Adres & Woonplaats" 
                                    desc="Laat klanten hun adres invullen."
                                    checked={!!formSettings?.askAddress}
                                    onChange={(val) => updateFormSettings({ ...(formSettings || {}), askAddress: val })}
                                />
                                <FormToggle 
                                    label="Vraag om Tennisclub" 
                                    desc="Handig voor statistieken of clubkortingen."
                                    checked={!!formSettings?.askClub}
                                    onChange={(val) => updateFormSettings({ ...(formSettings || {}), askClub: val })}
                                />
                                <FormToggle 
                                    label="Vraag om Huidige Snaar" 
                                    desc="Vraag welke snaar er nú in het racket zit."
                                    checked={!!formSettings?.askCurrentString}
                                    onChange={(val) => updateFormSettings({ ...(formSettings || {}), askCurrentString: val })}
                                />
                                <FormToggle 
                                    label="Meerdere Rackets Toestaan" 
                                    desc="Klanten kunnen in 1x tot 5 rackets aanmelden. Het systeem knipt dit automatisch op in losse opdrachten voor jou."
                                    checked={!!formSettings?.allowMultipleRackets}
                                    onChange={(val) => updateFormSettings({ ...(formSettings || {}), allowMultipleRackets: val })}
                                />
                            </div>
                        </div>

                        <div className="pt-6 border-t border-slate-100 dark:border-slate-700">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-3">Jouw Persoonlijke Link (Voor Bespanningen)</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Kies een eigen Link-ID (bijv. je naam) zodat je een makkelijk te delen link hebt.</p>
                            
                            <div className="mb-4">
                                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Kies jouw Link-ID</label>
                                <div className="flex gap-2">
                                    <input 
                                        type="text" 
                                        placeholder="bijv. janjansen"
                                        value={draftLinkId} 
                                        onChange={(e) => setDraftLinkId(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                                        className="flex-1 p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
                                    />
                                    <button 
                                        onClick={saveCustomLinkId}
                                        className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition shadow-sm"
                                    >
                                        Opslaan
                                    </button>
                                </div>
                                <p className="text-[10px] text-amber-600 dark:text-amber-500 mt-2 font-medium">Let op: als je dit aanpast, zie je aanvragen op je oude link niet meer terug in je inbox.</p>
                            </div>

                            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Kopieer jouw Link</label>
                            <div className="flex gap-2">
                                <input 
                                    type="text" 
                                    readOnly 
                                    value={shareLink} 
                                    className="flex-1 p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 text-sm focus:outline-none"
                                />
                                <button 
                                    onClick={() => {
                                        const textArea = document.createElement("textarea");
                                        textArea.value = shareLink;
                                        document.body.appendChild(textArea);
                                        textArea.select();
                                        try {
                                            document.execCommand('copy');
                                            alert("Jouw persoonlijke link is gekopieerd!");
                                        } catch (err) {
                                            console.error('Kopiëren mislukt', err);
                                        }
                                        document.body.removeChild(textArea);
                                    }} 
                                    className="px-5 py-3 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-white rounded-xl font-bold transition flex items-center justify-center"
                                >
                                    <ClipboardCopy className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    </div>
                );
                break;
                
            case 'data':
                title = 'Data Beheer';
                icon = <Database className="w-6 h-6 text-amber-500" />;
                content = (
                    <div className="text-center py-8 space-y-4">
                        <div className="w-16 h-16 bg-amber-50 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Database className="w-8 h-8 text-amber-500" />
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 dark:text-white">Importeer je geschiedenis</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm mx-auto mb-6">Heb je oude opdrachten in een ander systeem staan? Je kunt ze hier gemakkelijk importeren zodat al je data op één plek staat.</p>
                        <button 
                            onClick={() => { setImportText(''); setShowJobImportModal(true); }} 
                            className="w-full md:w-auto px-8 py-3 bg-indigo-600 hover:bg-indigo-700 transition text-white rounded-xl font-bold shadow-sm"
                        >
                            Importeer Historie
                        </button>
                    </div>
                );
                break;
        }

        return (
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 animate-in fade-in duration-300">
                <div className="flex items-center mb-8 pb-4 border-b border-slate-100 dark:border-slate-700">
                    <button 
                        onClick={() => setActiveSetting(null)} 
                        className="p-2 mr-4 bg-slate-100 dark:bg-slate-700 rounded-full hover:bg-slate-200 dark:hover:bg-slate-600 transition text-slate-600 dark:text-slate-300"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-slate-50 dark:bg-slate-900">
                            {icon}
                        </div>
                        <h2 className="text-2xl font-bold text-slate-800 dark:text-white">{title}</h2>
                    </div>
                </div>
                
                <div className="max-w-2xl mx-auto">
                    {content}
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-6 md:p-8">
            <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in duration-500">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                            <Settings className="w-6 h-6 text-indigo-500" />
                            Instellingen
                        </h1>
                        <p className="text-slate-500 dark:text-slate-400 mt-1">
                            Beheer je bedrijfsgegevens, koppelingen en bespanners.
                        </p>
                    </div>
                </div>

                {activeSetting ? renderDetailView() : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 animate-in fade-in duration-300">
                        
                        <div 
                            onClick={() => setActiveSetting('company')}
                            className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl p-5 shadow-sm flex flex-col transition-all hover:shadow-md hover:ring-2 hover:ring-indigo-500/50 cursor-pointer group"
                        >
                            <div className="flex items-center gap-4 mb-4">
                                <div className="p-3 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 group-hover:scale-110 transition-transform">
                                    <Briefcase className="w-6 h-6" />
                                </div>
                            </div>
                            <div className="mt-auto pt-2">
                                <p className="text-lg font-bold text-slate-900 dark:text-white">Bedrijfsinformatie</p>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Stel je bedrijfsnaam & logo in</p>
                            </div>
                        </div>

                        <div 
                            onClick={() => setActiveSetting('stringers')}
                            className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl p-5 shadow-sm flex flex-col transition-all hover:shadow-md hover:ring-2 hover:ring-indigo-500/50 cursor-pointer group"
                        >
                            <div className="flex items-center gap-4 mb-4">
                                <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform">
                                    <User className="w-6 h-6" />
                                </div>
                            </div>
                            <div className="mt-auto pt-2">
                                <p className="text-lg font-bold text-slate-900 dark:text-white">Bespanners</p>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{stringers.length} {stringers.length === 1 ? 'profiel' : 'profielen'} actief</p>
                            </div>
                        </div>

                        <div 
                            onClick={() => setActiveSetting('requests')}
                            className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl p-5 shadow-sm flex flex-col transition-all hover:shadow-md hover:ring-2 hover:ring-indigo-500/50 cursor-pointer group"
                        >
                            <div className="flex items-center gap-4 mb-4">
                                <div className="p-3 rounded-lg bg-pink-50 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 group-hover:scale-110 transition-transform">
                                    <CheckSquare className="w-6 h-6" />
                                </div>
                            </div>
                            <div className="mt-auto pt-2">
                                <p className="text-lg font-bold text-slate-900 dark:text-white">Aanvragen Accepteren</p>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Formulier & link beheren</p>
                            </div>
                        </div>

                        {/* NIEUW: Testrackets Tegel */}
                        <div 
                            onClick={() => setActiveSetting('testrackets')}
                            className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl p-5 shadow-sm flex flex-col transition-all hover:shadow-md hover:ring-2 hover:ring-purple-500/50 cursor-pointer group"
                        >
                            <div className="flex items-center gap-4 mb-4">
                                <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 group-hover:scale-110 transition-transform">
                                    <CalendarCheck className="w-6 h-6" />
                                </div>
                            </div>
                            <div className="mt-auto pt-2">
                                <p className="text-lg font-bold text-slate-900 dark:text-white">Testrackets (Borg)</p>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Publieke reserveringslink</p>
                            </div>
                        </div>

                        <div 
                            onClick={() => setActiveSetting('mollie')}
                            className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl p-5 shadow-sm flex flex-col transition-all hover:shadow-md hover:ring-2 hover:ring-indigo-500/50 cursor-pointer group"
                        >
                            <div className="flex items-center gap-4 mb-4">
                                <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform">
                                    <CreditCard className="w-6 h-6" />
                                </div>
                            </div>
                            <div className="mt-auto pt-2">
                                <p className="text-lg font-bold text-slate-900 dark:text-white">Mollie API</p>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Beheer betaallinks & keys</p>
                            </div>
                        </div>

                        <div 
                            onClick={() => setActiveSetting('data')}
                            className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl p-5 shadow-sm flex flex-col transition-all hover:shadow-md hover:ring-2 hover:ring-indigo-500/50 cursor-pointer group"
                        >
                            <div className="flex items-center gap-4 mb-4">
                                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 group-hover:scale-110 transition-transform">
                                    <Database className="w-6 h-6" />
                                </div>
                            </div>
                            <div className="mt-auto pt-2">
                                <p className="text-lg font-bold text-slate-900 dark:text-white">Data Beheer</p>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Importeer je historie</p>
                            </div>
                        </div>

                    </div>
                )}
            </div>
        </div>
    );
}